/**
 * Turning a message count into money.
 *
 * WP-CH-09's estimator answers in messages and stops there, because sam-dbam
 * exposed no rate schedule when it was built. This is the multiplication it
 * handed on. It is one step, and it is kept here rather than inside the
 * estimator so the estimator's rules and the price list stay independently
 * checkable.
 *
 * Nothing here guesses. A destination with no published rate is reported as
 * unpriced, never as free and never at a nearby country's price. A quoted number
 * that turns out to be wrong is worse than no number, because somebody plans
 * around it.
 */

/**
 * The rate row that applies, or null.
 *
 * Specific beats general, in both dimensions. A rate published for one country
 * wins over the catch-all row, and a rate published for one kind of message wins
 * over the catch-all kind. Reversing that would quote everybody the general
 * price and bill several of them a different one.
 */
export function findRate(rateRows = [], { channel, messageKind, destinationCountry } = {}) {
    if (!Array.isArray(rateRows) || !channel) return null;

    const forChannel = rateRows.filter((r) => r.channel === channel);
    if (forChannel.length === 0) return null;

    const wantedCountry = destinationCountry ? String(destinationCountry).trim().toUpperCase() : '';
    const countryOf = (r) => (r.destination_country ? String(r.destination_country).trim().toUpperCase() : '');

    const exactCountry = wantedCountry ? forChannel.filter((r) => countryOf(r) === wantedCountry) : [];
    const pool = exactCountry.length > 0
        ? exactCountry
        : forChannel.filter((r) => countryOf(r) === '' || countryOf(r) === 'ANY');
    if (pool.length === 0) return null;

    const wantedKind = messageKind ? String(messageKind).trim().toLowerCase() : '';
    const kindOf = (r) => (r.message_kind ? String(r.message_kind).trim().toLowerCase() : '');

    const exactKind = wantedKind ? pool.filter((r) => kindOf(r) === wantedKind) : [];
    if (exactKind.length > 0) return exactKind[0];

    const anyKind = pool.filter((r) => kindOf(r) === '' || kindOf(r) === 'any');
    return anyKind[0] || null;
}

/** Money, written the way a person writes it. */
export function formatMoney(amount, currency = 'USD') {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return '';
    // Two decimals is how money normally reads. Three is allowed because a
    // single message can cost less than a cent, and rounding $0.0087 to $0.01
    // overstates a large send by fifteen per cent.
    const number = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
    return currency === 'USD' ? `$${number}` : `${currency} ${number}`;
}

/**
 * Price a message-count estimate.
 *
 * The counts are echoed back alongside the money. A range exists because two
 * people answering a branching survey differently send different numbers of
 * messages, and quoting one number for both would be wrong for at least one of
 * them.
 */
export function priceEstimate(estimate = {}, rateRows = [], opts = {}) {
    const counts = {
        representative: estimate?.representative,
        minimum: estimate?.minimum,
        maximum: estimate?.maximum,
    };

    const rate = findRate(rateRows, opts);
    if (!rate) {
        return {
            priced: false,
            currency: '',
            perMessage: 0,
            representative: null,
            minimum: null,
            maximum: null,
            counts,
            note: 'No published rate covers this channel and destination, so the cost cannot be shown.',
        };
    }

    const perMessage = Number(rate.customer_price) || 0;
    const currency = rate.currency || 'USD';

    // Rounded to four places. Multiplying in binary floating point leaves dust
    // like 3.4000000000000004, and a total built out of those reads as broken.
    const money = (count) => (
        typeof count === 'number' && !Number.isNaN(count)
            ? Math.round(count * perMessage * 10000) / 10000
            : null
    );

    const each = formatMoney(perMessage, currency);
    // The note counts messages, not money. It sits beside the money figures and
    // says what they were worked out from.
    const note = counts.minimum === counts.maximum && counts.minimum !== undefined
        ? `${counts.minimum} messages at ${each} each.`
        : `Between ${counts.minimum ?? 0} and ${counts.maximum ?? 0} messages, at ${each} each.`;

    return {
        priced: true,
        currency,
        perMessage,
        representative: money(counts.representative),
        minimum: money(counts.minimum),
        maximum: money(counts.maximum),
        counts,
        note,
    };
}
